<template>
  <v-container fluid fill-height>
    <v-layout row>
      <v-flex xs12>
        <v-stepper v-model="currentStep" vertical>
          <v-stepper-step :complete="currentStep > 1" step="1">
            <template v-if="showFirstStep">
              {{ $t('create_tenant.step1.title') }}
              <small>{{ $t('create_tenant.step1.subtitle') }}</small>
            </template>
            <template v-else-if="hasEnabledBle">
              {{ $t('create_tenant.step1.title_forcedBLE') }}
            </template>
            <template v-else>
              {{ $t('create_tenant.step1.title_forcedRFID') }}
            </template>
          </v-stepper-step>

          <v-stepper-content step="1">
            <v-form ref="stepOneForm" v-model="validStepOne" lazy-validation>
              <v-select v-model="ownerTypeSelected" item-text="name" item-value="id" :items="ownerTypes" :rules="[rules.required]" required></v-select>
            </v-form>

            <v-layout row>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="$refs.stepOneForm.validate() ? (currentStep = 2) : (currentStep = 1)">
                {{ $t('create_tenant.button.continue') }}
              </v-btn>
            </v-layout>
          </v-stepper-content>

          <v-stepper-step :complete="currentStep > 2" step="2">
            <template v-if="ownerTypeSelected === 'with-email'">
              {{ $t('create_tenant.step2.title.withUser') }}
              <small>{{ $t('create_tenant.step2.subtitle') }}</small>
            </template>
            <template v-else>
              {{ $t('create_tenant.step2.title.withTag') }}
            </template>
          </v-stepper-step>

          <v-stepper-content step="2">
            <template v-if="isCheckedTeletag && !isValidTeletag">
              <v-alert value="true" outline color="error" icon="error" class="mb-4">{{ $t('create_tenant.step2.invalidTag') }}</v-alert>
            </template>
            <template v-if="isCheckedKeypad && isKeypadCodeAlreadyInUse">
              <v-alert value="true" outline color="error" icon="error" class="mb-4">{{ $t('create_tenant.step2.keypadInUse') }}</v-alert>
            </template>

            <v-form ref="stepTwoForm" v-model="validTwoForm" lazy-validation>
              <template v-if="ownerTypeSelected === 'with-email'">
                <v-text-field v-model="email" type="email" :label="$t('private_parking_tenants_table_email')" :rules="[rules.required, rules.email]" required></v-text-field>
              </template>
              <template v-else>
                <v-text-field v-model="teletag" :label="$t('private_global_teletag')" :rules="rules.teletag" required></v-text-field>
              </template>
              <v-text-field
                v-model="keypad"
                type="number"
                :label="$t('private_global_keypad')"
                :hint="$t('create_tenant.keypad.hint')"
                persistent-hint
                :rules="rules.keypad"
                required
              ></v-text-field>
            </v-form>

            <v-layout row>
              <v-spacer></v-spacer>
              <v-btn text v-if="showFirstStep" @click="backToStepOne()">
                {{ $t('create_tenant.button.back') }}
              </v-btn>
              <v-btn color="primary" @click="checkIfExists()" :loading="isLoading">
                {{ $t('create_tenant.button.continue') }}
              </v-btn>
            </v-layout>
          </v-stepper-content>

          <v-stepper-step :complete="currentStep > 3" step="3">
            {{ $t('create_tenant.step3.title') }}
            <small>{{ $t('create_tenant.step3.subtitle') }}</small>
          </v-stepper-step>

          <v-stepper-content step="3">
            <template v-if="isCheckedEmail">
              <v-alert :value="isExistsEmail" outline color="info" icon="info" class="mb-4">{{ $t('create_tenant.step3.userNotExists') }}</v-alert>
              <v-alert :value="!isExistsEmail" outline color="info" icon="info" class="mb-4">{{ $t('create_tenant.step3.userExists') }}</v-alert>
            </template>

            <v-form ref="stepThreeForm" v-model="validThreeForm" lazy-validation>
              <v-text-field v-model="name" :rules="[rules.required]" :label="$t('private_global_name')" required></v-text-field>
              <v-text-field v-model="spot" :label="$t('private_global_spot')"></v-text-field>
              <v-textarea v-model="description" rows="4" style="padding-top:25px" counter="250" :label="$t('private_global_description')"></v-textarea>

              <v-layout row wrap>
                <v-flex xs12>
                  <vuetify-from-to-date-selector ref="fromToDateSelector" :lang-selected="langSelected" :prop-start="start" :prop-end="end" :allowSameStartEnd="false" :allowNullEnd="true" @on-dates-selected="datesSelected" />
                </v-flex>
              </v-layout>
            </v-form>

            <v-layout row>
              <v-spacer></v-spacer>
              <v-btn text @click="backToStepTwo()">
                {{ $t('create_tenant.button.back') }}
              </v-btn>
              <v-btn color="primary" @click="submit()" :loading="isLoading">
                {{ $t('create_tenant.button.create') }}
              </v-btn>
            </v-layout>
          </v-stepper-content>
        </v-stepper>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import store from '@/store';
import moment from 'moment';
import keys from '@/store/keys';
import { EventBus } from '@/services/eventBus';
import tenantService from '@/services/tenantService';
import VuetifyFromToDateSelector from '@/components/commons/VuetifyFromToDateSelector.vue';

export default {
  name: 'CreateTenant',

  components: {
    [VuetifyFromToDateSelector.name]: VuetifyFromToDateSelector
  },
  computed: {
    hasEnabledBle() {
      return store.state.parkingSelected.hasBLE;
    },

    hasEnabledRfid() {
      return store.state.parkingSelected.hasRFID;
    },

    ownerTypes() {
      let ownerTypes = [];

      if (this.hasEnabledRfid) {
        ownerTypes.push({ id: 'with-tag', name: this.$i18n.t('global.teletag') });
        this.ownerTypeSelected = 'with-tag';
      }

      if (this.hasEnabledBle) {
        ownerTypes.push({ id: 'with-email', name: this.$i18n.t('global.bluetooth') });
        this.ownerTypeSelected = 'with-email';
      }

      if (ownerTypes.length === 1) {
        this.currentStep = 2;
        this.showFirstStep = false;
      }

      return ownerTypes;
    }
  },
  data() {
    return {
      rules: {
        required: value => !!value || this.$i18n.t('private_validation_required_name'),
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || this.$i18n.t('global.validation.email');
        },
        teletag: [
          v => {
            if (typeof v === 'undefined') {
              return this.$i18n.t('private_validation_required_teletag');
            }
            return !!v || v.trim().length === 0 || this.$i18n.t('private_validation_required_teletag');
          },
          v => {
            if (typeof v === 'undefined') {
              return this.$i18n.t('private_validation_required_teletag');
            }
            return (v && v.trim().length === 5) || this.$i18n.t('private_validation_size_teletag');
          }
        ],
        keypad: [
          v => {
            if (typeof v === 'undefined') {
              return this.$i18n.t('private_validation_required_keypad');
            }
            return !!v || v.trim().length === 0 || this.$i18n.t('private_validation_required_keypad');
          },
          v => {
            if (typeof v === 'undefined') {
              return this.$i18n.t('private_validation_required_keypad');
            }
            return (v && !isNaN(v.trim())) || this.$i18n.t('private_validation_required_keypad');
          }
        ]
      },
      stepNumber: 1,
      showFirstStep: true,
      langSelected: store.state.langSelected,
      currentStep: 1,
      validStepOne: true,
      ownerTypeSelected: null,
      email: '',
      name: '',
      spot: '',
      description: '',
      start: null,
      end: null,
      isLoading: false,
      teletag: '',
      keypad: '',
      isCheckedTeletag: false,
      isEnabledTeletag: false,
      isValidTeletag: false,
      isCheckedKeypad: false,
      isKeypadCodeAlreadyInUse: false,
      isCheckedEmail: false,
      isExistsEmail: false
    };
  },

  created() {
    this.reset();
    //    this.ownerTypes = ownerTypes;
  },

  methods: {
    async checkIfExists() {
      if (!this.$refs.stepTwoForm.validate()) {
        return;
      }

      this.isLoading = true;
      if (this.ownerTypeSelected === 'with-email') {
        await this.checkIfEmailExists();
        if (await this.checkIfKeypadExists()) {
          this.currentStep = 3;
          this.isLoading = false;
          return;
        }
      } else {
        if ((await this.checkIfTeletagExists()) && (await this.checkIfKeypadExists())) {
          this.currentStep = 3;
          this.isLoading = false;
          return;
        }
      }

      this.currentStep = 2;
      this.isLoading = false;
    },

    async checkIfTeletagExists() {
      let payload = {
        parkingId: Number(this.$route.params.id),
        code: this.teletag
      };

      let resp = await tenantService.verifyTag(payload);
      this.isCheckedTeletag = true;

      if (resp.error) {
        return false;
      } else {
        this.isValidTeletag = resp.data.exists;
        this.isEnabledTeletag = resp.data.enabled;

        if (resp.data.exists) {
          return true;
        } else {
          return false;
        }
      }
    },

    async checkIfKeypadExists() {
      let payload = {
        parkingId: Number(this.$route.params.id),
        keypad: this.keypad,
        username: this.email
      };

      let resp = await tenantService.verifyKeypad(payload);
      this.isCheckedKeypad = true;

      if (resp.error) {
        return false;
      } else {
        this.isKeypadCodeAlreadyInUse = resp.data.exists;

        if (resp.data.exists) {
          return false;
        } else {
          return true;
        }
      }
    },

    async checkIfEmailExists() {
      let payload = {
        parkingId: Number(this.$route.params.id),
        email: this.email
      };

      let resp = await tenantService.verifyEmail(payload);
      this.isCheckedEmail = true;
      this.isExistsEmail = resp.data.exists;

      if (resp.error) {
        return false;
      } else {
        if (resp.data.exists) {
          return true;
        } else {
          return false;
        }
      }
    },

    datesSelected(params) {
      if (params.start) {
        this.start = params.start;
      }

      this.end = params.end;
    },

    reset() {
      this.stepNumber = 1;
      this.showFirstStep = true;
      this.currentStep = 1;
      this.validStepOne = true;
      this.ownerTypeSelected = null;
      this.name = '';
      this.spot = '';
      this.description = '';
      this.start = null;
      this.end = null;
      this.isLoading = false;
      this.resetStep2();
    },

    resetStep2() {
      this.validStepOne = true;
      this.email = '';
      this.teletag = '';
      this.keypad = '';
      this.isCheckedTeletag = false;
      this.isValidTeletag = false;
      this.isEnabledTeletag = false;
      this.isKeypadCodeAlreadyInUse = false;
      this.isCheckedKeypad = false;
      this.isCheckedEmail = false;
      this.isExistsEmail = false;
    },

    async submit() {
      if (this.$refs.stepThreeForm.validate()) {
        this.isLoading = true;

        let params = {
          start: moment(this.start).format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
          end: null,
          keypad: this.keypad,
          name: this.name,
          spot: this.spot,
          description: this.description
        };

        if (this.end) {
          params.end = moment(this.end).format('YYYY-MM-DD[T]HH:mm:ss[Z]');
        }

        params.parkingId = Number(this.$route.params.id);

        let resp;
        if (this.ownerTypeSelected === 'with-email') {
          params.email = this.email;
          resp = await tenantService.createInvitation(params);

          if (resp.error) {
            EventBus.$emit(keys.eventOnShowNotification, {
              message: this.$i18n.t('private_global_error'),
              type: 'error',
              doNotTranslate: true
            });
            this.isLoading = false;
            return;
          }
        } else {
          params.label = this.teletag;
          resp = await tenantService.createTeletagTenant(params);

          if (resp.error) {
            if (resp.status === 412) {
              EventBus.$emit(keys.eventOnShowNotification, {
                message: resp.data.error,
                type: 'error',
                doNotTranslate: true
              });
            } else {
              EventBus.$emit(keys.eventOnShowNotification, {
                message: this.$i18n.t('private_global_error'),
                type: 'error',
                doNotTranslate: true
              });
            }
            this.isLoading = false;
            return;
          }
        }

        if (this.ownerTypeSelected === 'with-email') {
          if (this.isExistsEmail) {
            EventBus.$emit(keys.eventOnShowNotification, {
              message: this.$i18n.t('private_owner_create_success'),
              type: 'success',
              doNotTranslate: true
            });
          } else {
            EventBus.$emit(keys.eventOnShowNotification, {
              message: this.$i18n.t('private_owner_send_invitation_success'),
              type: 'success',
              doNotTranslate: true
            });

            EventBus.$emit(keys.eventOnTenantInvitationCreated);
          }
        } else {
          EventBus.$emit(keys.eventOnShowNotification, {
            message: this.$i18n.t('private_owner_teletag_added_success'),
            type: 'success',
            doNotTranslate: true
          });
        }

        this.reset();
        this.$emit('close');
      }
    },

    backToStepOne() {
      this.currentStep = 1;
      this.resetStep2();
    },

    backToStepTwo() {
      this.currentStep = 2;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/parking/CreateTenant.vue